import { useEffect, useCallback } from 'react'
import config from 'constants/config'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import jwt_decode from 'jwt-decode'
import { GSILoadComplete, loginGoogle } from 'actions/AuthActions'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'

interface GoogleToken {
  email: string;
  family_name: string;
  given_name: string;
  sub: string;
}

interface GoogleInitializeData {
  client_id: string;
  auto_select?: boolean;
  callback?: (response: unknown) => void;
  ux_mode?: string;
  login_uri?: string;
}

declare global {
  interface Window {
      onGoogleLibraryLoad: () => void;
      google: {
        accounts: {
          id: {
            initialize: (data: GoogleInitializeData) => unknown;
            renderButton: (document: HTMLElement, props?: any) => unknown;
          }
        }
      };
  }
}

export default function GoogleAuth() {
  const urlSearchParams = useQueryParams()
  const dispatch = useAppDispatch()
  const whiteLabelAppConfig = useWhiteLabelAppConfig()

  const onGoogleSignedIn = useCallback((response) => {
    if (!response?.credential) throw new Error('Google Login error')

    const googleData: GoogleToken = jwt_decode(response.credential)

    const data = {
      id_google: googleData.sub,
      email: googleData.email,
      token_id: response.credential,
      givenName: googleData.given_name,
      surname: googleData.family_name,
    }

    dispatch(loginGoogle(data))
  }, [dispatch])

  useEffect(() => {
    const onGoogleLoad = (whiteLabelAppGoogleLoginURI = '') => {
      dispatch(GSILoadComplete())

      const params = {
        client_id: config.GOOGLE_APP_ID,
        auto_select: true,
        ...(whiteLabelAppGoogleLoginURI ?
            { ux_mode: 'redirect', login_uri: whiteLabelAppGoogleLoginURI } :
            { callback: onGoogleSignedIn }),
      }
      window.google.accounts.id.initialize(params)
    }

    if (window.google?.accounts) {
      // must already be loaded by the time we've mounted, don't need to hook into load function
      onGoogleLoad(whiteLabelAppConfig.googleLoginURI)

      if (whiteLabelAppConfig.googleLoginURI) {
        const credential = urlSearchParams.get('credential') as string
        if (credential) onGoogleSignedIn({ credential })
      }
    } else {
      // not loaded yet, hook into the callback
      window.onGoogleLibraryLoad = onGoogleLoad
    }
  }, [dispatch, onGoogleSignedIn, urlSearchParams, whiteLabelAppConfig.googleLoginURI])

  return null
}
